/**
 * What the new-conversation page offers and sends: the preset starts, the quick-start subject types and the start
 * request. Pure, so the request shape is tested without rendering the page.
 */
import type { LucideIcon } from 'lucide-react';
import { Bot, ClipboardList, FileText, GitBranch, Route, ShoppingBag, Users } from 'lucide-react';
import type { ConversationQuickStart } from '../../generated/api/types.gen.js';

/** Shared preset-chip shape for the "Recommended starts" panel (generic + finance). */
export interface IntentPreset {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  message: string;
  templateSlug?: string;
}

type QuickStartSubjectType = NonNullable<ConversationQuickStart['subjectType']>;

const QUICK_START_SUBJECT_TYPES: readonly QuickStartSubjectType[] = [
  'daemon-draft',
  'workflow',
  'template',
  'tool',
  'project',
  'project_thread',
  'project_change',
];

export function isQuickStartSubjectType(value: string | null): value is QuickStartSubjectType {
  return QUICK_START_SUBJECT_TYPES.some((type) => type === value);
}

export function buildGenericPresets(): IntentPreset[] {
  return [
    {
      id: 'project',
      label: 'Create project',
      description: 'Turn an idea into a project draft with first thread and change.',
      icon: FileText,
      message:
        'Turn this idea into a project draft. Propose a root ProjectThread, first ProjectChange, expected artifacts, tasks, workflows, workforce roles, and decisions. Present the full bundle for review before anything is promoted.',
    },
    {
      id: 'workflow',
      label: 'Draft workflow',
      description: 'Shape a repeatable process into steps and contracts.',
      icon: Route,
      message:
        'Draft a workflow for this process. Include trigger, steps, input and output contracts, approval gates, expected artifacts, and what should happen before it is used. Keep it reviewable until approved.',
    },
    {
      id: 'marketplace',
      label: 'Find capability',
      description: 'Search for templates, tools, workflows, or workforce options.',
      icon: ShoppingBag,
      message:
        'Search the library and marketplace for templates, tools, workflows, or workforce roles that fit this need. Compare options and recommend what to install, adapt, or defer.',
    },
    {
      id: 'clarify',
      label: 'Clarify next step',
      description: 'Ask only for missing context before creating drafts.',
      icon: Bot,
      message:
        'Clarify the next best action for this conversation. Ask only for missing essentials, then recommend whether to create a project, task, workflow, artifact, workforce plan, marketplace recommendation, or decision draft.',
    },
  ];
}

export function buildProjectPresets(projectName: string): IntentPreset[] {
  return [
    {
      id: 'change',
      label: 'Draft change',
      description: 'Create a branch-like unit before tasks, checks, or merge.',
      icon: GitBranch,
      message: `Draft the next ProjectChange for "${projectName}". Include target functionality, work mode, ProjectThread context, branch/check expectations, expected artifacts, and keep it in draft until admin review, validation, and readiness checks are complete.`,
    },
    {
      id: 'task',
      label: 'Create task',
      description: 'Turn bounded project work into an accountable task.',
      icon: ClipboardList,
      message: `Create a task draft for "${projectName}" from this conversation. Include owner expectations, inputs, outputs, deadline, blockers, review gate, and the ProjectChange it belongs to. Present it for approval before dispatch.`,
    },
    {
      id: 'workflow',
      label: 'Draft workflow',
      description: 'Capture repeated project work as a procedure.',
      icon: Route,
      message: `Draft a project-scoped workflow for "${projectName}". Include trigger, steps, contracts, approval gates, generated artifacts, and how it connects to the current ProjectChange. Keep execution gated by admin approval.`,
    },
    {
      id: 'workforce',
      label: 'Plan workforce',
      description: 'Recommend roles, mandates, tools, and first assignments.',
      icon: Users,
      message: `Recommend workforce roles for "${projectName}". Include responsibilities, needed tools, permission boundaries, reporting line, and first assignments tied to ProjectChanges. Present hires and assignments as a reviewable bundle.`,
    },
    {
      id: 'artifact',
      label: 'Refine artifact',
      description: 'Create or improve a project artifact with provenance.',
      icon: FileText,
      message: `Refine the next artifact for "${projectName}". Identify whether this should become requirements, design, architecture, implementation plan, validation report, launch notes, or support evidence, and present the version for review before promotion.`,
    },
    {
      id: 'clarify',
      label: 'Clarify next step',
      description: 'Find the smallest useful action before creating work.',
      icon: Bot,
      message: `Clarify the next best action for "${projectName}". Summarize current context, ask only for missing essentials, then recommend one reviewable draft bundle for changes, tasks, workflows, workforce, artifacts, marketplace options, or decisions.`,
    },
  ];
}

/** Where the conversation is being started from (the page's query string). */
export interface StartContext {
  projectId: string | null;
  teamId: string | null;
  parentConversationId: string | null;
  subjectType: string | null;
  subjectId: string | null;
}

/**
 * The start request. It carries the user's module scope and the surface — the same scope the simplified surface sends —
 * so the same request opens on the same persona from both. No persona is pinned unless a preset chose one, and no
 * runtime policy until one is picked (the default is resolved server-side).
 */
export function buildStartRequest(input: {
  message: string;
  moduleId: string;
  context: StartContext;
  templateSlug: string | undefined;
  runtimePolicyId: number | null;
}): ConversationQuickStart {
  const { message, moduleId, context, templateSlug, runtimePolicyId } = input;
  const subjectType = isQuickStartSubjectType(context.subjectType) ? context.subjectType : null;
  return {
    message: message.trim(),
    moduleId,
    surface: 'detailed',
    ...(context.projectId ? { projectId: context.projectId } : {}),
    ...(context.teamId ? { teamId: context.teamId } : {}),
    ...(context.parentConversationId ? { parentConversationId: context.parentConversationId } : {}),
    ...(subjectType ? { subjectType } : {}),
    ...(subjectType && context.subjectId ? { subjectId: context.subjectId } : {}),
    ...(templateSlug ? { templateSlug } : {}),
    ...(runtimePolicyId !== null ? { runtimePolicyId } : {}),
  };
}
